import { IntegrationInitializationModal } from '../IntegrationInitializationModal'
import { IntegrationNotice } from '../IntegrationNotice'
import { useModals } from 'almanac-ui/Modal'
import Styles from '../IntegrationInitializationModal.module.scss'
import { LinkedIn } from 'almanac-ui/icons'
import { IntegrationInitializationDrawer } from '../IntegrationInitializationDrawer'
import { InitializationProps } from '../types'
import { useInitiateOauth } from 'hooks/useInitiateOauth'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { BarChartSquare, Calendar2, Send1 } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import cx from 'classnames'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

const ComparisonRow = ({
  icon,
  label,
  personalSupported = true,
  companySupported = true,
}: {
  icon: React.ReactNode
  label: string
  personalSupported?: boolean
  companySupported?: boolean
}) => {
  const { isMobile } = useResponsiveDesign()
  return (
    <div className={Styles.row}>
      <div className={cx(Styles.cell, !personalSupported && Styles.disabled)}>
        {icon}
        <Text variant="placeholder" color="inherit">
          {label}
          {!personalSupported && !isMobile && ' not supported'}
        </Text>
      </div>
      <div className={Styles.cell}>
        {icon}
        <span className={!companySupported ? Styles.disabled : ''}>{label}</span>
      </div>
    </div>
  )
}

export const LinkedInNotice = () => (
  <IntegrationNotice
    connectionGuideLink="https://help.blaze.ai/en/articles/9559677-connect-blaze-to-linkedin"
    message={
      <div className={cx(Styles.message, Styles.linkedin)}>
        <Text color="var(--dark-80)" variant="placeholder">
          LinkedIn functionality varies between personal accounts and page accounts.
        </Text>

        <div className={Styles.comparisonTable}>
          <div className={Styles.tableHeader}>
            <div>Personal Account</div>
            <div>Company page</div>
          </div>

          <ComparisonRow icon={<Calendar2 size={16} />} label="Auto Schedule" />
          <ComparisonRow icon={<Send1 size={16} className={Styles.icon} />} label="Auto-Post" />
          <ComparisonRow
            icon={<BarChartSquare size={16} className={Styles.icon} />}
            label="Analytics"
            personalSupported={false}
          />
        </div>

        <div className={Styles.note}>If you are not sure if you have the right setup, review our guide:</div>
      </div>
    }
  />
)

const LinkedInInitialization = ({ userIsAttemptingToPost = false, useDrawer = false }: InitializationProps) => {
  const { closeModal } = useModals()
  const { initiateOauth } = useInitiateOauth()

  const title = userIsAttemptingToPost ? 'Set up your LinkedIn integration' : 'Connect to LinkedIn'

  const sharedProps = {
    notice: <LinkedInNotice />,
    providerName: 'LinkedIn',
    onClose: closeModal,
    icon: <LinkedIn size={32} />,
    title,
    onConfirm: () => initiateOauth(ExternalIntegrationEnum.LinkedIn),
    primaryButtonText: 'Go to Connect LinkedIn',
  }

  return useDrawer ? (
    <IntegrationInitializationDrawer {...sharedProps} />
  ) : (
    <IntegrationInitializationModal {...sharedProps} />
  )
}

export default LinkedInInitialization
